export const NOTIFICATION_VARIABLES = [
  {
    key: "%customer_name%",
    label: "Customer Name",
    fallback: "there",
    maxLength: 50,
    description: "The name of the customer receiving the notification",
    note: "If customer name is not available, 'there' will be displayed",
  },
  {
    key: "%city%",
    label: "City",
    fallback: "your area",
    maxLength: 30,
    description: "The city of the customer",
    note: "If city is not available, 'your area' will be displayed",
  },
  {
    key: "%store_name%",
    label: "Store Name",
    fallback: "our store",
    maxLength: 50,
    description: "The name of your store/outlet",
    note: "If store name is not available, 'our store' will be displayed",
  },
  {
    key: "%customer_email%",
    label: "Customer Email",
    fallback: "customer",
    maxLength: 50,
    description: "The email of the customer",
    note: "If customer email is not available, 'customer' will be displayed",
  },
];

// Alias map: backend may send %restaurant_name% which should be treated as %store_name%
const VARIABLE_ALIASES = {
  "%restaurant_name%": "%store_name%",
};

const normalizeVariables = (text) => {
  if (!text) return text;
  let result = text;
  for (const [alias, canonical] of Object.entries(VARIABLE_ALIASES)) {
    result = result.replace(new RegExp(alias.replace(/%/g, "\\$&"), "g"), canonical);
  }
  return result;
};

export const substituteVariables = (
  text,
  variables = {},
  useFallback = false
) => {
  if (!text) return "";

  let result = normalizeVariables(text);

  NOTIFICATION_VARIABLES.forEach((variable) => {
    const regex = new RegExp(variable.key.replace(/%/g, "\\$&"), "g");
    const value = useFallback
      ? variable.fallback
      : variables[variable.key] || variable.fallback;

    result = result.replace(regex, value || "");
  });

  result = result.replace(/\s+/g, " ").trim();
  result = result.replace(/\s+([.,!?])/g, "$1");

  return result;
};

export const getDummyVariables = () => {
  return {
    "%customer_name%": "John Doe",
    "%customer_email%": "john.doe@example.com",
    "%city%": "New York",
    "%store_name%": "Pizza Palace Downtown",
  };
};

export const getMaxLengthWithVariables = (text) => {
  if (!text) return 0;
  const normalized = normalizeVariables(text);
  let maxLength = normalized.length;

  NOTIFICATION_VARIABLES.forEach((variable) => {
    const regex = new RegExp(variable.key.replace(/%/g, "\\$&"), "g");
    const matches = normalized.match(regex);
    if (matches) {
      const variableLength = variable.key.length;
      const maxVariableLength = variable.maxLength;
      maxLength += matches.length * (maxVariableLength - variableLength);
    }
  });

  return maxLength;
};

export const getCharacterCountInfo = (text, limit) => {
  const currentLength = text ? text.length : 0;
  const maxPossibleLength = getMaxLengthWithVariables(text);

  return {
    current: currentLength,
    maxPossible: maxPossibleLength,
    limit: limit,
    exceedsLimit: maxPossibleLength > limit,
    remaining: limit - maxPossibleLength,
  };
};

export const extractVariablesFromText = (text) => {
  if (!text) return [];

  const normalized = normalizeVariables(text);
  return NOTIFICATION_VARIABLES.filter((variable) => {
    const regex = new RegExp(variable.key.replace(/%/g, "\\$&"), "g");
    return regex.test(normalized);
  });
};

export const validateVariableSyntax = (text) => {
  const normalized = normalizeVariables(text);
  // Find all %...% patterns in the text
  const variableRegex = /%[^%]+%/g;
  const allVariables = normalized.match(variableRegex) || [];

  const validKeys = NOTIFICATION_VARIABLES.map((v) => v.key);
  const invalidVariables = allVariables.filter((v) => !validKeys.includes(v));

  if (invalidVariables.length > 0) {
    return {
      isValid: false,
      message: `Unknown variables: ${invalidVariables.join(
        ", "
      )}. Valid variables are: ${validKeys.join(", ")}`,
    };
  }

  return { isValid: true };
};

export const insertVariableAtPosition = (text, variable, cursorPosition) => {
  if (!text) {
    return {
      newText: variable,
      newCursorPosition: variable.length,
    };
  }

  const before = text.substring(0, cursorPosition);
  const after = text.substring(cursorPosition);

  const needsSpaceBefore = before.length > 0 && !before.endsWith(" ");
  const needsSpaceAfter = after.length > 0 && !after.startsWith(" ");

  let insertion = variable;
  if (needsSpaceBefore) insertion = " " + insertion;
  if (needsSpaceAfter) insertion = insertion + " ";

  return {
    newText: before + insertion + after,
    newCursorPosition: cursorPosition + insertion.length,
  };
};
